import { useEffect, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';

/**
 * Emits the Datadog RUM "Showed Alert Box" action once per distinct alert while
 * the alert is visible. Re-fires when the alert identity (alertId/alertName)
 * changes so a single mounted component that swaps between alerts tracks each
 * one, but never re-fires on an unrelated re-render. Use useAlertTracking when a
 * single call should report to both GA4 and Datadog RUM.
 *
 * @param {Object}  options
 * @param {string}  options.alertName  — human-readable identifier used in the RUM action name
 * @param {string}  options.status     — 'error' | 'warning' | 'info' | 'success'
 * @param {string}  [options.alertId]  — stable identifier included in the RUM context
 * @param {boolean} [options.isVisible=true] — whether the alert is currently rendered
 * @param {Object}  [options.context={}] — free-form bag merged into the RUM action's context.
 *   Read via a ref so callers don't need to memoize the object literal.
 */
const useAlertRumTracking = ({
  alertName,
  status,
  alertId,
  isVisible = true,
  context = {},
}) => {
  const trackedKey = useRef(null);
  const contextRef = useRef(context);
  contextRef.current = context;

  useEffect(
    () => {
      const key = `${alertId ?? ''}::${alertName ?? ''}`;
      if (isVisible && alertName && trackedKey.current !== key) {
        datadogRum.addAction(`Showed Alert Box: ${alertName}`, {
          alertId,
          status,
          ...contextRef.current,
        });
        trackedKey.current = key;
      }
    },
    // `context` is intentionally excluded — it's typically a fresh object literal per render,
    // which would re-fire this effect needlessly. Read the latest value via contextRef instead.
    [isVisible, alertName, status, alertId],
  );
};

export default useAlertRumTracking;
